import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * LSD #167: the drawer opens by itself only after one of the shop's own Add buttons.
 *
 * CartAddEvent.eventName is ThemeEvents.cartUpdate: the theme's scripts send that same `cart:update`
 * event when they change the items in the cart and when they re-read it, so without this list the
 * drawer also opened on events the shopper never caused: the cart refresh after Back (source
 * 'bfcache-restore', from snippets/cart-drawer.liquid's pageshow handler), and Shipping Protection's
 * and the free gift's own cart changes.
 *
 * product-form-component covers the product page's buttons, quick add and the size buttons on
 * cards; ugc-immersive and ugc-lightbox are the community photo sections. A new Add button that
 * sends its own cart:update must be added here, or it will not open the drawer.
 * One deliberate exception, outside this cart:update path: after a sign-in that started in the
 * drawer, assets/cart-free-gift.js (openCartAfterSignIn) opens it by calling open() (#221).
 * See docs/business-logic.md section 21.
 */
const AUTO_OPEN_SOURCES = new Set(['product-form-component', 'ugc-immersive', 'ugc-lightbox']);

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    if (!AUTO_OPEN_SOURCES.has(event.detail?.data?.source)) return;
    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
